import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "wouter";
import { cn } from "@/lib/utils";
import { formatRelativeTime } from "@/lib/relative-time";
import { fetchNotifications, markAsRead, markAllAsRead } from "@/services/notification.service";
import type { Notification } from "@/types/notification.types";

const TYPE_COLORS: Record<Notification["type"], string> = {
  alert: "bg-red-500",
  error: "bg-red-500",
  warning: "bg-yellow-500",
  info: "bg-blue-500",
};

interface NotificationDropdownProps {
  open: boolean;
  onClose: () => void;
  onCountChange: () => void;
}

export function NotificationDropdown({ open, onClose, onCountChange }: NotificationDropdownProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [, navigate] = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetchNotifications({ limit: 10 });
    if (res.ok && res.data) {
      setNotifications(res.data.items);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (open) {
      load();
    }
  }, [open, load]);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        // Check if click was on the bell button (parent handles toggle)
        const target = e.target as HTMLElement;
        if (target.closest("[data-notification-bell]")) return;
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, onClose]);

  const handleMarkAllRead = async () => {
    const res = await markAllAsRead();
    if (res.ok) {
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      onCountChange();
    }
  };

  const handleClickItem = async (notification: Notification) => {
    if (!notification.isRead) {
      const res = await markAsRead(notification.id);
      if (res.ok) {
        setNotifications((prev) => prev.map((n) => (n.id === notification.id ? { ...n, isRead: true } : n)));
        onCountChange();
      }
    }
    onClose();
    navigate("/notificacoes");
  };

  if (!open) return null;

  return (
    <div
      ref={dropdownRef}
      className="absolute right-0 top-full mt-2 w-96 bg-popover border border-border rounded-lg shadow-lg z-50 overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <h3 className="text-sm font-semibold text-foreground">Notificações</h3>
        <button
          onClick={handleMarkAllRead}
          className="text-xs text-primary hover:text-primary/80 cursor-pointer"
        >
          Marcar todas como lidas
        </button>
      </div>

      {/* List */}
      <div className="max-h-96 overflow-y-auto">
        {loading ? (
          <div className="p-4 text-center text-sm text-muted-foreground">Carregando...</div>
        ) : notifications.length === 0 ? (
          <div className="p-4 text-center text-sm text-muted-foreground">Nenhuma notificação</div>
        ) : (
          notifications.map((n) => (
            <button
              key={n.id}
              onClick={() => handleClickItem(n)}
              className={cn(
                "w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors border-b border-border last:border-b-0 cursor-pointer flex gap-3 items-start",
                !n.isRead && "bg-muted/30",
              )}
            >
              {/* Type dot */}
              <div className={cn("w-2 h-2 rounded-full mt-1.5 shrink-0", TYPE_COLORS[n.type])} />

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className={cn("text-sm truncate", !n.isRead ? "font-semibold text-foreground" : "text-foreground/80")}>
                    {n.title}
                  </span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-xs text-muted-foreground">{formatRelativeTime(n.createdAt)}</span>
                    {!n.isRead && <div className="w-2 h-2 rounded-full bg-primary shrink-0" />}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground truncate mt-0.5">{n.message}</p>
              </div>
            </button>
          ))
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-border px-4 py-2.5">
        <button
          onClick={() => {
            onClose();
            navigate("/notificacoes");
          }}
          className="text-xs text-primary hover:text-primary/80 cursor-pointer w-full text-center"
        >
          Ver todas as notificacoes →
        </button>
      </div>
    </div>
  );
}
